import { PdfCitedReply } from './PdfCitedReply';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Send, BookOpen, ArrowRight } from 'lucide-react';
import { TutorAvatar, DEFAULT_TUTOR_NAME } from '@/components/tutor/TutorAvatar';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { StudyContextTools } from './StudyContextTools.tsx';
import { ApiError } from '@/lib/api';
import { askStudyTutor, startStudySession, getStudySession, getStudyTutorPrice } from './studyApi';
import { useStudyAvailability, subjectAvailability } from './studyAvailability';
import { tutorStateMessage } from './studyAccessMessages';
import { StudyPdfTutorSetup } from './StudyPdfTutorSetup';
import {
  readPdfLearning,
  preparePdfLearning,
  parsedPdfGuide,
  type PdfLearningGuide,
} from './studyPdfLearningApi';
const schema = z.object({ question: z.string().trim().min(1).max(1200) });
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'PDF learning could not load. Please retry.';
function Guide({ guide, onPage }: { guide: PdfLearningGuide; onPage: (page: number) => void }) {
  return (
    <section aria-label="PDF learning guide" className="space-y-3">
      <h3 className="text-sm font-extrabold text-ink">{guide.title}</h3>
      <p className="text-xs leading-relaxed text-ink-soft">{guide.overview}</p>
      <p className="text-[11px] text-slate2">
        AI learning suggestions · check the linked PDF pages.
      </p>
      {guide.steps.map((step, i) => (
        <article key={i} className="rounded-2xl border border-hairline bg-canvas p-3">
          <h4 className="text-xs font-bold text-ink">
            {i + 1}. {step.title}
          </h4>
          <p className="mt-2 text-xs leading-relaxed text-ink-soft">{step.goal}</p>
          <p className="mt-2 text-xs leading-relaxed text-slate2">Try: {step.activity}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {step.pages.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => onPage(page)}
                className="inline-flex min-h-11 items-center gap-1 rounded-xl bg-wash-sky px-3 text-xs font-bold text-ink"
              >
                Page {page}
                <ArrowRight className="h-3 w-3" />
              </button>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
}
export function StudyPdfDocumentTutor({
  documentId,
  fileName,
  active,
  onBusyChange,
  onPage,
}: {
  documentId: string;
  fileName: string;
  active: boolean;
  onBusyChange: (busy: boolean) => void;
  onPage: (page: number) => void;
}) {
  const owner = useAuthStore(() => getKidSub());
  const scope = `${owner}:${documentId}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const isCurrent = (requestScope: string) =>
    mounted.current && currentScope.current === requestScope && getKidSub() === owner;
  const client = useQueryClient();
  const [checkedDigest, setCheckedDigest] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<{ scope: string; text: string; id?: string } | null>(
    null,
  );
  const latest = delivery?.scope === scope ? delivery.text : null;
  const pane = useRef<HTMLDivElement>(null);
  const keys = useRef(new Map<string, string>());
  const form = useForm<{ question: string }>({
    resolver: zodResolver(schema),
    defaultValues: { question: '' },
  });
  const { reset } = form;
  useEffect(() => {
    setCheckedDigest(null);
    setDelivery(null);
    reset();
  }, [scope, reset]);
  const learningKey = ['pdf-learning', owner, documentId];
  const learning = useQuery({
    queryKey: learningKey,
    queryFn: async () => {
      const stored = await readPdfLearning(documentId);
      if (stored.index) return stored;
      await preparePdfLearning(documentId);
      return readPdfLearning(documentId);
    },
    enabled: active,
    retry: false,
    staleTime: Infinity,
  });
  const sessionKey = ['pdf-document-chat', owner, documentId];
  const session = useQuery({
    queryKey: sessionKey,
    queryFn: async () => {
      const saved = await startStudySession(documentId);
      if (getKidSub() !== owner || saved.document.id !== documentId)
        throw new Error('This saved conversation does not belong to the current PDF.');
      return saved;
    },
    enabled: active && Boolean(learning.data?.index),
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const price = useQuery({
    queryKey: ['study-tutor-price', owner],
    queryFn: getStudyTutorPrice,
    enabled: active,
    retry: false,
  });
  const availability = useStudyAvailability(active);
  const data = session.data;
  const index = learning.data?.index;
  const unread =
    index?.pages.filter((page) => page.state !== 'text').map((page) => page.page) ?? [];
  const guideTooLong = learning.data?.guide_available === false && !unread.length;
  const readable = index?.pages.filter((page) => page.state === 'text').length ?? 0;
  const policy = subjectAvailability(availability.data, data?.subject.subject_key ?? '');
  const paused = policy && policy.tutor.state !== 'available' ? tutorStateMessage(policy) : null;
  const needsGuidance = Boolean(data?.subject_tutor && data.subject_tutor.status !== 'published');
  const needsCheck =
    availability.data?.policy.require_source_confirmation === true &&
    checkedDigest !== index?.digest;
  const available = Boolean(
    data &&
    index &&
    readable &&
    price.data &&
    availability.data &&
    !price.isError &&
    !availability.isError &&
    !session.isError &&
    !paused &&
    !needsGuidance &&
    !needsCheck &&
    policy?.enabled !== false &&
    availability.data.study_visible !== false &&
    data.status === 'active',
  );
  const send = useMutation({
    mutationFn: async ({ question, guide }: { question: string; guide: boolean }) => {
      if (!available || !data || !index || !price.data)
        throw new Error('Check PDF reading and Tutor access first.');
      const hash = JSON.stringify({
        id: data.id,
        digest: index.digest,
        question,
        guide,
        stars: price.data.stars,
      });
      let id = keys.current.get(hash);
      if (!id) {
        id = crypto.randomUUID();
        keys.current.set(hash, id);
      }
      const reply = await askStudyTutor(data.id, {
        question,
        hint_level: 0,
        expected_stars: price.data.stars,
        idempotency_key: id,
        source_scope: 'document',
        document_action: guide ? 'guide' : 'question',
        ...(checkedDigest === index.digest ? { document_digest: checkedDigest } : {}),
      });
      keys.current.delete(hash);
      return { reply, requestScope: scope };
    },
    onSuccess: async ({ reply, requestScope }, variables) => {
      if (!isCurrent(requestScope)) return;
      setDelivery({ scope: requestScope, text: reply.reply, id: reply.assistant_message_id });
      if (!variables.guide) form.reset();
      const guide = parsedPdfGuide(reply.reply);
      if (guide) client.setQueryData(learningKey, { ...learning.data, guide });
      if (data) {
        try {
          const saved = await getStudySession(data.id);
          if (isCurrent(requestScope) && saved.id === data.id && saved.document.id === documentId)
            client.setQueryData(sessionKey, saved);
        } catch {
          /* Keep the paid reply visible if history read-back fails. */
        }
      }
    },
  });
  useEffect(() => {
    onBusyChange(send.isPending);
    return () => onBusyChange(false);
  }, [send.isPending, onBusyChange]);
  useEffect(() => {
    if (pane.current && (latest || send.isPending)) {
      const plan = latest && parsedPdfGuide(latest);
      const card = pane.current.querySelector('[aria-label="PDF learning guide"]');
      if (plan && card)
        pane.current.scrollTop +=
          card.getBoundingClientRect().top - pane.current.getBoundingClientRect().top;
      else pane.current.scrollTop = pane.current.scrollHeight;
    }
  }, [latest, send.isPending]);
  const question = form.watch('question');
  const guide = learning.data?.guide;
  const messages = data?.messages.filter((item) => !parsedPdfGuide(item.content)) ?? [];
  const savedReply =
    latest && !parsedPdfGuide(latest)
      ? messages.find((item) => item.role === 'assistant' && item.id === delivery?.id)
      : messages.filter((item) => item.role === 'assistant').at(-1);
  const canLinkReply = Boolean(savedReply);
  return (
    <section aria-label="Whole PDF Tutor" className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b border-hairline bg-wash-sky/50 px-4 py-3">
        <TutorAvatar size="small" expression={send.isPending ? 'thinking' : 'friendly'} />
        <div>
          <h3 className="text-sm font-extrabold text-ink">Ask {DEFAULT_TUTOR_NAME}</h3>
          <p className="text-xs text-slate2">Understand and learn this PDF</p>
        </div>
      </header>
      <div
        ref={pane}
        role="region"
        aria-label="PDF Tutor messages"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3"
      >
        {guide && !needsCheck && <Guide guide={guide} onPage={onPage} />}
        <div className="rounded-2xl border border-hairline bg-canvas p-3">
          <p className="hidden truncate text-xs font-bold text-ink md:block">{fileName}</p>
          {learning.isPending && (
            <p role="status" className="mt-2 text-xs text-slate2">
              Reading the PDF text page by page… No AI charge.
            </p>
          )}
          {index && (
            <p className="mt-2 text-xs leading-relaxed text-slate2">
              {readable} / {index.pages.length} pages indexed · PDF text. Questions use relevant
              passages; annotations stay private.
            </p>
          )}
          {unread.length > 0 && (
            <p role="status" className="mt-2 text-xs text-danger-600">
              Pages {unread.join(', ')} need text recognition. The guide is unavailable until they
              can be read; questions can use readable pages only.
            </p>
          )}
        </div>
        {learning.isError && (
          <div role="alert" className="rounded-xl bg-wash-coral p-3 text-xs">
            {errorText(learning.error)}
            <button
              type="button"
              onClick={() => void learning.refetch()}
              className="btn-pill-secondary mt-2 min-h-11 text-xs"
            >
              Retry PDF reading
            </button>
          </div>
        )}
        {session.isError && (
          <div role="alert" className="rounded-xl bg-wash-coral p-3 text-xs">
            {errorText(session.error)}
            {session.error instanceof ApiError &&
            session.error.code === 'STUDY_DOCUMENT_NOT_ORGANISED' ? (
              <StudyPdfTutorSetup documentId={documentId} onReady={() => void session.refetch()} />
            ) : (
              <button
                type="button"
                className="btn-pill-secondary mt-2 min-h-11 text-xs"
                onClick={() => void session.refetch()}
              >
                Retry conversation
              </button>
            )}
          </div>
        )}
        {index && (
          <details className="rounded-xl border border-hairline p-3" open={needsCheck}>
            <summary className="min-h-11 cursor-pointer text-xs font-bold text-ink">
              PDF text and reading coverage {needsCheck ? '(check required)' : ''}
            </summary>
            <p className="py-2 text-xs text-slate2">
              For diagrams or handwriting, choose This page image. The whole-PDF index contains
              readable text.
            </p>
            <div className="max-h-48 space-y-3 overflow-y-auto py-2">
              {index.pages.map((page) => (
                <div key={page.page}>
                  <button
                    type="button"
                    onClick={() => onPage(page.page)}
                    className="min-h-11 text-xs font-bold text-brand-sky"
                  >
                    Page {page.page}
                  </button>
                  <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-slate2">
                    {page.text || 'No readable text on this page.'}
                  </p>
                </div>
              ))}
            </div>
            {needsCheck && (
              <button
                type="button"
                onClick={() => setCheckedDigest(index.digest)}
                className="btn-pill-secondary mt-2 min-h-11 text-xs"
              >
                I checked this PDF text
              </button>
            )}
          </details>
        )}
        {(paused ||
          needsGuidance ||
          availability.data?.study_visible === false ||
          policy?.enabled === false) && (
          <p role="status" className="rounded-xl bg-wash-sunshine p-3 text-xs text-ink">
            {paused ||
              (needsGuidance
                ? 'Tutor guidance for this subject is not published yet. Your PDF and notes still work.'
                : 'Tutor is unavailable for this account.')}
          </p>
        )}
        {guideTooLong && (
          <p role="status" className="rounded-xl bg-wash-sunshine p-3 text-xs text-ink">
            The whole PDF is indexed for questions. For a complete learning guide, upload one
            chapter or a shorter document.
          </p>
        )}
        {guide ? (
          needsCheck ? (
            <Guide guide={guide} onPage={onPage} />
          ) : null
        ) : (
          <div className="rounded-2xl bg-wash-mint/40 p-3">
            <h3 className="text-sm font-bold text-ink">Where should I start?</h3>
            <p className="mt-2 text-xs leading-relaxed text-slate2">
              Get an overview and an ordered learning path, with page links and a small activity for
              each step.
            </p>
            <button
              type="button"
              disabled={!available || unread.length > 0 || guideTooLong || send.isPending}
              onClick={() =>
                send.mutate({
                  question:
                    'Explain what this PDF is about and create an ordered learning guide grounded in its contents.',
                  guide: true,
                })
              }
              className="btn-pill-secondary mt-3 min-h-11 w-full gap-2 text-xs"
            >
              <BookOpen className="h-4 w-4" />
              Create learning guide{price.data ? ` · ${price.data.stars}★` : ''}
            </button>
          </div>
        )}
        {messages.map((item) => (
          <article
            key={item.id}
            className={
              item.role === 'user'
                ? 'ml-4 rounded-2xl bg-ink p-3 text-white'
                : 'mr-2 rounded-2xl bg-surface p-3 text-ink'
            }
          >
            <p className="mb-1 text-[11px] font-bold">
              {item.role === 'user' ? 'You' : DEFAULT_TUTOR_NAME}
            </p>
            <PdfCitedReply text={item.content} pages={index?.pages.length ?? 0} onPage={onPage} />
          </article>
        ))}
        {latest && !parsedPdfGuide(latest) && !messages.some((item) => item.content === latest) && (
          <div className="rounded-2xl bg-surface p-3 text-ink">
            <PdfCitedReply text={latest} pages={index?.pages.length ?? 0} onPage={onPage} />
          </div>
        )}
        {!send.isPending && data && data.goal !== 'assessment' && canLinkReply && (
          <StudyContextTools
            compact
            disabled={Boolean(question?.trim())}
            context={{
              subjectKey: data.subject.subject_key ?? '',
              label: `${fileName} · saved PDF explanation`,
              text: savedReply!.content,
              returnTo: `/learn/study/sessions/${encodeURIComponent(data.id)}?message=${encodeURIComponent(savedReply!.id)}`,
            }}
          />
        )}
        {send.isPending && (
          <p role="status" className="text-xs text-slate2">
            Tutor is reading the relevant passages…
          </p>
        )}
        {send.error && (
          <p role="alert" className="rounded-xl bg-wash-coral p-3 text-xs">
            {errorText(send.error)} Your question stays here for retry.
          </p>
        )}
      </div>
      <form
        onSubmit={form.handleSubmit((values) =>
          send.mutate({ question: values.question, guide: false }),
        )}
        className="shrink-0 border-t border-hairline bg-canvas-pure p-3"
      >
        <p className="mb-2 text-[11px] text-slate2">
          {price.data
            ? `${price.data.stars}★ per message · whole PDF search`
            : 'Checking Tutor price…'}
        </p>
        {(price.isError || availability.isError) && (
          <p role="alert" className="mb-2 text-xs">
            Tutor access or price could not load.
            <button
              type="button"
              className="min-h-11 px-2 font-bold underline"
              onClick={() => {
                void price.refetch();
                void availability.refetch();
              }}
            >
              Retry access
            </button>
          </p>
        )}
        <label className="sr-only" htmlFor="pdf-document-question">
          Ask about this PDF
        </label>
        <textarea
          id="pdf-document-question"
          {...form.register('question')}
          rows={2}
          maxLength={1200}
          disabled={send.isPending}
          placeholder="What is this PDF about? What should I learn first?"
          className="block max-h-28 min-h-16 w-full resize-y rounded-xl border border-hairline bg-canvas p-3 text-sm text-ink focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate2">
            {needsGuidance
              ? 'Waiting for published subject guidance'
              : needsCheck
                ? 'Check the PDF text above first'
                : 'Relevant passages · original PDF only'}
          </p>
          <button
            type="submit"
            disabled={!available || !question.trim() || send.isPending}
            className="btn-pill-primary min-h-11 gap-2 px-4 text-xs"
          >
            {send.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send
          </button>
        </div>
      </form>
    </section>
  );
}
